import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faPen, faCheckCircle, faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { anchovy, iconGrey } from 'lib/css/colors';
import BrandContentQAIcon from 'components/manage_brands/BrandContentQAIcon';
import EditableProperty from 'components/manage_brands/brand_details/EditableProperty';
import { faSquareCheck } from 'images/icons/fsa-monotone-svg-icons';
import pointerOnHover from 'lib/css/pointerOnHover';
import { SimilarBrandNamesStatus, StatusColors } from 'lib/enums/SimilarBrandNamesStatusEnums';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import { normalizeUrl } from 'lib/utils/stringUtils';
function BrandRow({ label, title, brand, updateBrand, refetch }) {
  const hasUserPermissions = useUserPermissions();
  const [isEditing, setIsEditing] = useState(false);
  const [updatedBrand, setUpdatedBrand] = useState({ id: brand.id });

  const canEditLockedAttributes = useMemo(
    () => hasUserPermissions([UserPermission.ADMIN_OPERATIONS]),
    [hasUserPermissions],
  );

  function getTooltipText(status) {
    switch (status) {
      case SimilarBrandNamesStatus.FIRST_YES:
        return 'Approved once';
      case SimilarBrandNamesStatus.SECOND_YES:
        return 'Approved twice';
      case SimilarBrandNamesStatus.THIRD_YES:
        return 'Verified';
      case SimilarBrandNamesStatus.FIRST_NO:
        return 'Rejected once';
      case SimilarBrandNamesStatus.SECOND_NO:
        return 'Rejected twice';
      case SimilarBrandNamesStatus.TIED_1:
        return '1 Approval, 1 Rejection';
      case SimilarBrandNamesStatus.TWO_YES_ONE_NO:
        return 'Approvals - 2, Rejections - 1';
      case SimilarBrandNamesStatus.TWO_NO_ONE_YES:
        return 'Approvals - 1, Rejections - 2';
      case SimilarBrandNamesStatus.TIED:
        return 'Awaiting final decision';
      default:
        return 'Not reviewed yet';
    }
  }

  return (
    <div>
      <p className={css(styles.label)}>{title}</p>
      {isEditing ? (
        <Form
          onBlur={() => {
            if (label !== 'similar_brands' && label !== 'video_urls') {
              setIsEditing(false);
            }
          }}
          className={label === 'similar_brands' ? css(styles.rowNotEditable) : css(styles.row)}
        >
          {label !== 'similar_brands' && label !== 'video_urls' ? (
            <FontAwesomeIcon
              className={classNames(pointerOnHover, css(styles.editIcon))}
              icon={faSquareCheck}
              onMouseDown={() => {
                updateBrand(updatedBrand);
                setIsEditing(false);
              }}
              size="lg"
            />
          ) : null}
          <EditableProperty
            label={label}
            brand={brand}
            setIsEditing={setIsEditing}
            updatedBrand={updatedBrand}
            setUpdatedBrand={setUpdatedBrand}
            updateBrand={updateBrand}
            refetch={refetch}
          />
        </Form>
      ) : (
        <div className={css(styles.row)}>
          {brand.locked_attributes.includes(label) ? (
            canEditLockedAttributes ? (
              <OverlayTrigger overlay={<Tooltip>Locked Attribute</Tooltip>}>
                <FontAwesomeIcon
                  className={classNames(pointerOnHover, css(styles.editIcon))}
                  icon={faLock}
                  onClick={() => setIsEditing(true)}
                />
              </OverlayTrigger>
            ) : (
              <BrandContentQAIcon brand={brand} attribute={label} />
            )
          ) : (
            <FontAwesomeIcon
              className={classNames(pointerOnHover, css(styles.editIcon))}
              icon={faPen}
              onClick={() => setIsEditing(true)}
            />
          )}
          {label === 'description' ? (
            <div>
              {brand[label] ? (
                brand[label].split('\n').map((paragraph, index) => <p key={index}>{paragraph}</p>)
              ) : (
                <p>-</p>
              )}
            </div>
          ) : label === 'similar_brands' ? (
            <div>
              {brand[label].length ? (
                brand[label].map(similarBrand => (
                  <p key={similarBrand.id}>
                    &bull; {similarBrand.brand_name}
                    <OverlayTrigger
                      overlay={<Tooltip>{getTooltipText(similarBrand.status)}</Tooltip>}
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="ml-2"
                        style={{
                          color: StatusColors[similarBrand.status] || iconGrey,
                        }}
                      />
                    </OverlayTrigger>
                  </p>
                ))
              ) : (
                <p>-</p>
              )}
            </div>
          ) : label === 'video_urls' ? (
            <div>
              {brand[label]?.length ? (
                brand[label].map(video_url => <p key={video_url.id}>&bull; {video_url}</p>)
              ) : (
                <p>-</p>
              )}
            </div>
          ) : label === 'brand_url' ? (
            brand[label] ? (
              <a href={normalizeUrl(brand[label])} target="_blank" rel="noopener noreferrer">
                {brand[label]}
              </a>
            ) : (
              <p>-</p>
            )
          ) : label === 'manufacturer_id' ? (
            brand['global_manufacturer'] ? (
              <p
                className={pointerOnHover}
                onClick={() =>
                  window.open(
                    `/enrich-products/manufacturer-details/${brand['global_manufacturer']['id']}`,
                    '_blank',
                  )
                }
              >
                {brand['global_manufacturer']['name'] || '-'}
              </p>
            ) : (
              <p>-</p>
            )
          ) : (
            <p>{brand[label] || '-'}</p>
          )}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  label: {
    display: 'inline',
    fontWeight: 'bold',
    color: anchovy,
  },
  row: {
    display: 'flex',
    marginLeft: '-1.65rem',
  },
  rowNotEditable: {
    display: 'flex',
  },
  editIcon: {
    marginRight: '0.75rem',
  },
});

BrandRow.propTypes = {
  label: PropTypes.string,
  title: PropTypes.string,
  brand: PropTypes.object,
  updateBrand: PropTypes.func,
  refetch: PropTypes.func,
};

export default BrandRow;
